import React, { useState } from 'react';
import { Plus, Settings, User, X } from 'lucide-react';
import { BankFieldsForm, EMPTY_BANK_FORM, UserSelect } from './shared';
import { useLocale } from '../../contexts/LocaleContext';

export default function PerUserBankPanel({ accounts, onSave, onDelete, onRemoveUser }) {
  const { t } = useLocale();
  const [showForm, setShowForm] = useState(false);
  const [pickedUsers, setPickedUsers] = useState([]);
  const [form, setForm] = useState(EMPTY_BANK_FORM);
  const [editId, setEditId] = useState(null);
  const [editUsers, setEditUsers] = useState([]);

  const rows = [];
  accounts.forEach((acc) => {
    (acc.assignedUsers || []).forEach((u) => { rows.push({ ...u, account: acc }); });
  });

  const reset = () => {
    setForm(EMPTY_BANK_FORM);
    setEditId(null);
    setPickedUsers([]);
    setEditUsers([]);
    setShowForm(false);
  };

  const startEdit = (acc) => {
    setForm({
      accountHolder: acc.account_holder,
      bankName: acc.bank_name,
      accountNumber: acc.account_number,
      routingNumber: acc.routing_number || '',
      currency: acc.currency || 'USD',
      isDefault: false,
    });
    setEditId(acc.id);
    setEditUsers((acc.assignedUsers || []).map((u) => u.user_id));
    setShowForm(true);
  };

  const handleSubmit = async () => {
    const users = editId ? editUsers : pickedUsers;
    await onSave(editId, form, users);
    reset();
  };

  const uniqueAccounts = accounts.filter((a) => (a.assignedUsers || []).length > 0);

  return (
    <div className="space-y-2">
      {rows.length > 0 && (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div
              key={`${r.user_id}-${r.account.id}`}
              className="flex items-center gap-3 px-3 py-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg"
            >
              <User className="w-4 h-4 text-[var(--text-tertiary)] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-[var(--text-primary)]">
                  {r.user_name || r.user_email || `User #${r.user_id}`}
                  <span className="ml-1.5 text-[var(--text-tertiary)] font-normal">#{r.user_id}</span>
                </p>
                <p className="text-xs text-[var(--text-secondary)]">
                  {r.account.bank_name} · {r.account.currency} · ••••{String(r.account.account_number).slice(-4)}
                </p>
              </div>
              <button
                onClick={() => onRemoveUser(r.account.id, r.user_id)}
                className="p-1 rounded text-[var(--text-tertiary)] hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-smooth"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {uniqueAccounts.length > 0 && !showForm && (
        <div className="border-t border-[var(--border-color)] pt-2 mt-1">
          <p className="text-xs text-[var(--text-secondary)] mb-1.5">{t('plugins.editExistingAccount')}</p>
          <div className="flex flex-wrap gap-1.5">
            {uniqueAccounts.map((acc) => (
              <button
                key={acc.id}
                type="button"
                onClick={() => startEdit(acc)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-[var(--border-color)] text-xs text-[var(--text-secondary)] hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)] transition-smooth"
              >
                <Settings className="w-3 h-3" />
                {acc.bank_name} ••••{String(acc.account_number).slice(-4)}
              </button>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="border border-[var(--accent-primary)] rounded-lg p-3 bg-[var(--accent-primary)]/5 space-y-2">
          <p className="text-xs font-semibold text-[var(--text-primary)]">
            {editId ? t('plugins.editLabel') : t('plugins.newLabel')} {t('plugins.perUserBankAccount')}
          </p>

          <div>
            <p className="text-xs text-[var(--text-secondary)] mb-1">
              {editId ? t('plugins.assignedUsers') : t('plugins.pickTraders')}
            </p>
            <div className="flex flex-wrap gap-1 mb-1.5">
              {(editId ? editUsers : pickedUsers).map((uid) => (
                <span
                  key={uid}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--accent-primary)]/10 text-xs text-[var(--accent-primary)]"
                >
                  #{uid}
                  <button
                    type="button"
                    onClick={() => {
                      if (editId) setEditUsers((prev) => prev.filter((u) => u !== uid));
                      else setPickedUsers((prev) => prev.filter((u) => u !== uid));
                    }}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
            </div>
            <UserSelect
              value={null}
              onChange={(uid) => {
                if (editId) { if (!editUsers.includes(uid)) setEditUsers((prev) => [...prev, uid]); }
                else { if (!pickedUsers.includes(uid)) setPickedUsers((prev) => [...prev, uid]); }
              }}
              placeholder={t('plugins.searchAddTrader')}
            />
          </div>

          <BankFieldsForm form={form} setForm={setForm} showDefault={false} />

          <div className="flex gap-2 pt-1">
            <button
              onClick={handleSubmit}
              disabled={(editId ? editUsers : pickedUsers).length === 0}
              className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-lg hover:bg-[var(--accent-primary-dark)] text-xs disabled:opacity-40 transition-smooth"
            >
              {editId ? t('plugins.saveLabel') : t('plugins.addLabel')}
            </button>
            <button
              onClick={reset}
              className="px-4 py-2 border border-[var(--border-color)] rounded-lg hover:bg-[var(--bg-secondary)] text-xs text-[var(--text-primary)] transition-smooth"
            >
              {t('plugins.cancelLabel')}
            </button>
          </div>
        </div>
      )}

      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-1.5 text-xs text-[var(--accent-primary)] hover:text-[var(--accent-primary-dark)] transition-smooth"
        >
          <Plus className="w-3.5 h-3.5" />
          {t('plugins.addUserBankMapping')}
        </button>
      )}
    </div>
  );
}